import { computeFloatingPosition } from "./editorContext.mjs";
import {
  COMMAND_PRESENTATION,
  SELECTION_GROUPS,
  createCommandIcon,
  getBlockTriggerPresentation
} from "./editorCommandPresentation.mjs";
import { BACKGROUND_COLORS, TEXT_COLORS } from "./formattingConfig.mjs";
import {
  captureScrollOrigin,
  getScrollAncestors,
  readSafeRect,
  readViewportRect,
  rectIntersectsViewport,
  translateRectByScroll
} from "./scrollDelta.mjs";

const QUICK_BLOCK_TYPES = Object.freeze([
  "paragraph", "heading-1", "heading-2", "heading-3", "heading-4", "heading-5"
]);
const MENU_BLOCK_TYPES = Object.freeze([
  "ordered-list", "bullet-list", "task-list", "code-block", "blockquote", "horizontal-rule"
]);
const MENU_ALIGNMENT_COMMANDS = Object.freeze([
  "align-left", "align-center", "align-right", "indent-increase", "indent-decrease"
]);
const SERVICE_COMMANDS = Object.freeze(["block-cut", "block-copy", "block-delete"]);
const LINK_STATE_PAYLOAD = Object.freeze({ href: "https://example.invalid/" });

function safeCommands(editor, context) {
  try {
    const commands = editor?.getCommandsForContext?.(context);
    return Array.isArray(commands) ? commands : [];
  } catch {
    return [];
  }
}

function safeState(editor, command, payload) {
  try {
    const state = editor?.getCommandState?.(command.id, payload);
    return {
      active: state?.active === "mixed" ? "mixed" : state?.active === true,
      enabled: state?.enabled === true
    };
  } catch {
    return { active: false, enabled: false };
  }
}

function statePayload(command, payload) {
  return command?.id === "link" && payload?.href === undefined ? LINK_STATE_PAYLOAD : payload;
}

function validSelection(selection) {
  try {
    return Number.isInteger(selection?.from)
      && Number.isInteger(selection?.to)
      && selection.from < selection.to;
  } catch {
    return false;
  }
}

function viewportRect(viewport) {
  return readViewportRect(viewport);
}

function surfaceSize(element) {
  try {
    const rect = element.getBoundingClientRect();
    const width = Number.isFinite(rect?.width) ? rect.width : rect.right - rect.left;
    const height = Number.isFinite(rect?.height) ? rect.height : rect.bottom - rect.top;
    return {
      width: Number.isFinite(width) && width >= 0 ? width : 0,
      height: Number.isFinite(height) && height >= 0 ? height : 0
    };
  } catch {
    return { width: 0, height: 0 };
  }
}

function applyPosition(element, position) {
  if (!position) return;
  element.style.left = `${position.left}px`;
  element.style.top = `${position.top}px`;
  if (position.placement) element.dataset.placement = position.placement;
}

export function createContextualEditorUi({
  editor,
  elements,
  services = {},
  onAi = () => {},
  onToast = () => {},
  onCloseSlash = () => {},
  viewport,
  requestLink,
  readContextOnResize
} = {}) {
  const blockTypeTrigger = elements?.blockTypeTrigger;
  const blockMenu = elements?.blockMenu;
  const selectionToolbar = elements?.selectionToolbar;
  const selectionToolbarCommands = elements?.selectionToolbarCommands;
  const selectionToolbarPanels = elements?.selectionToolbarPanels;
  const slashMenu = elements?.slashMenu;
  const aiBubble = elements?.aiBubble;
  if (!blockTypeTrigger || !blockMenu || !selectionToolbar || !selectionToolbarCommands
    || !selectionToolbarPanels || !slashMenu || !aiBubble) {
    throw new TypeError("contextual editor elements are required");
  }

  const document = blockTypeTrigger.ownerDocument;
  const window = document?.defaultView || globalThis.window;
  const readViewport = typeof viewport === "function"
    ? viewport
    : () => document.documentElement.getBoundingClientRect();
  const askForLink = typeof requestLink === "function"
    ? requestLink
    : () => window?.prompt?.("输入链接地址", "https://") ?? null;
  const readResizeContext = typeof readContextOnResize === "function" ? readContextOnResize : null;
  let snapshot = null;
  let disposed = false;
  let epoch = 0;
  let blockMenuOpen = false;
  let activeSelectionGroup = null;
  let scrollOrigins = null;
  let observedScrollAncestors = new Set();

  function captureSurfaceScrollOrigins() {
    scrollOrigins = {
      blockTrigger: captureScrollOrigin(window, blockTypeTrigger),
      blockMenu: captureScrollOrigin(window, blockMenu),
      selectionToolbar: captureScrollOrigin(window, selectionToolbar)
    };
  }

  function toast(message) {
    if (disposed || typeof message !== "string" || !message) return;
    try { onToast(message); } catch {}
  }

  function commandPayload(command, context, payload = {}, source = snapshot) {
    return {
      ...payload,
      selection: source?.selection,
      block: source?.block,
      context
    };
  }

  async function runUiCommand(command, context, payload = {}) {
    if (disposed || !snapshot || !command) return;
    const runEpoch = epoch;
    const runSnapshot = snapshot;
    let effectivePayload = payload;
    if (command.id === "link" && payload.href === undefined) {
      const state = safeState(editor, command, statePayload(command, payload));
      if (state.active === true || state.active === "mixed") effectivePayload = { href: null };
      else {
        let href = null;
        try { href = askForLink(runSnapshot.selection); } catch {}
        if (href === null || href === undefined) return;
        effectivePayload = { href };
      }
    }
    const fullPayload = commandPayload(command, context, effectivePayload, runSnapshot);
    let result;
    let failureMessage = null;
    try {
      result = command.kind === "service"
        ? await services?.[command.id]?.(fullPayload)
        : await editor?.runCommand?.(command.id, fullPayload);
    } catch (error) {
      failureMessage = typeof error?.message === "string" && error.message ? error.message : "操作失败，请重试";
    }
    if (disposed || epoch !== runEpoch || snapshot !== runSnapshot) return;
    if (failureMessage) toast(failureMessage);
    else if (result?.ok === false && result.message) toast(result.message);
    activeSelectionGroup = null;
    renderSelectionToolbar(runSnapshot);
    if (blockMenuOpen) renderBlockMenu(runSnapshot);
  }

  function makeCommandButton(command, context, payload = {}, options = {}) {
    const button = document.createElement("button");
    const state = safeState(editor, command, statePayload(command, payload));
    button.type = "button";
    button.dataset.command = command.id;
    if (payload.color) {
      button.dataset.color = payload.color;
      button.className = "format-color-swatch";
      button.style.background = payload.color;
      button.setAttribute("aria-label", `${command.label} ${payload.color}`);
    } else {
      const presentation = COMMAND_PRESENTATION[command.id] || {
        icon: "more",
        tooltip: command.label || command.id,
        label: command.shortLabel || ""
      };
      button.classList.add("editor-icon-button");
      button.setAttribute("aria-label", presentation.tooltip);
      button.title = presentation.tooltip;
      button.dataset.tooltip = presentation.tooltip;
      if (options.hideIcon !== true) button.append(createCommandIcon(document, command.id));
      const visibleLabel = options.label || (options.showLabel === true ? presentation.label : "");
      if (visibleLabel) {
        const label = document.createElement("span");
        label.className = options.labelClass || "editor-icon-button__label";
        label.textContent = visibleLabel;
        button.append(label);
      }
    }
    button.disabled = !state.enabled;
    button.classList.toggle("is-active", state.active === true);
    button.classList.toggle("is-mixed", state.active === "mixed");
    button.classList.toggle("is-danger", command.id === "block-delete");
    if (context === "block") {
      button.setAttribute("role", command.kind === "service" ? "menuitem" : "menuitemcheckbox");
      if (command.kind !== "service") button.setAttribute("aria-checked", String(state.active));
    } else {
      button.setAttribute("aria-pressed", String(state.active));
    }
    button.addEventListener("click", () => { void runUiCommand(command, context, payload); });
    return button;
  }

  function paletteButtons(command, context, palette) {
    return palette.map((color) => makeCommandButton(command, context, { color }));
  }

  function makeSelectionGroupOpener(group) {
    const opener = document.createElement("button");
    opener.type = "button";
    opener.className = "editor-icon-button selection-toolbar__group-trigger";
    opener.dataset.selectionGroup = group.id;
    opener.dataset.tooltip = group.tooltip;
    opener.setAttribute("aria-label", group.tooltip);
    opener.title = group.tooltip;
    opener.setAttribute("aria-haspopup", "menu");
    opener.setAttribute("aria-expanded", String(activeSelectionGroup === group.id));
    opener.setAttribute("aria-controls", `selection-toolbar-panel-${group.id}`);
    opener.append(createCommandIcon(document, group.icon));
    opener.addEventListener("click", () => {
      activeSelectionGroup = activeSelectionGroup === group.id ? null : group.id;
      renderSelectionToolbar(snapshot);
    });
    return opener;
  }

  function makeColorSection(commandId, command) {
    const section = document.createElement("section");
    section.className = "selection-toolbar__popover-section";
    if (!command) return section;
    const heading = document.createElement("span");
    heading.className = "selection-toolbar__popover-label";
    heading.textContent = commandId === "text-color" ? "文字颜色" : "背景颜色";
    const grid = document.createElement("div");
    grid.className = "selection-toolbar__color-grid";
    const palette = commandId === "text-color" ? TEXT_COLORS : BACKGROUND_COLORS;
    grid.append(...paletteButtons(command, "selection", palette));
    section.append(heading, grid);
    return section;
  }

  function makeSelectionPanel(group, byId) {
    const panel = document.createElement("div");
    panel.id = `selection-toolbar-panel-${group.id}`;
    panel.className = `selection-toolbar__popover selection-toolbar__popover--${group.id}`;
    panel.dataset.selectionGroupPanel = group.id;
    panel.setAttribute("role", "menu");
    for (const commandId of group.commands) {
      if (commandId === "text-color" || commandId === "background-color") {
        panel.append(makeColorSection(commandId, byId.get(commandId)));
        continue;
      }
      const command = byId.get(commandId);
      if (!command) continue;
      const presentation = COMMAND_PRESENTATION[commandId];
      const button = makeCommandButton(command, "selection", {}, {
        label: presentation?.label || command.label || command.shortLabel || command.id
      });
      if (commandId === "reset-colors") button.classList.add("selection-toolbar__color-reset");
      panel.append(button);
    }
    return panel;
  }

  function placeFloating(element, anchor, view = viewportRect(readViewport)) {
    if (!rectIntersectsViewport(anchor, view)) return;
    const position = computeFloatingPosition({
      anchor,
      surface: surfaceSize(element),
      viewport: view
    });
    applyPosition(element, position);
  }

  function placeBlockTrigger(next, view = viewportRect(readViewport)) {
    if (!rectIntersectsViewport(next?.blockRect, view)) return;
    const surface = surfaceSize(blockTypeTrigger);
    const gap = 8;
    const maximumLeft = Math.max(view.left, view.right - surface.width);
    const maximumTop = Math.max(view.top, view.bottom - surface.height);
    const left = Math.max(view.left, Math.min(next.blockRect.left - surface.width - gap, maximumLeft));
    const centeredTop = next.blockRect.top + (next.blockRect.bottom - next.blockRect.top - surface.height) / 2;
    const top = Math.max(view.top, Math.min(centeredTop, maximumTop));
    applyPosition(blockTypeTrigger, { left, top });
  }

  function placeBlockMenu(view = viewportRect(readViewport)) {
    if (!view) return;
    let anchor;
    try { anchor = readSafeRect(blockTypeTrigger.getBoundingClientRect()); } catch { return; }
    if (!anchor || !rectIntersectsViewport(anchor, view)) return;
    const surface = surfaceSize(blockMenu);
    const gap = 8;
    const maximumLeft = Math.max(view.left, view.right - surface.width);
    const left = Math.max(view.left, Math.min(anchor.left, maximumLeft));
    const belowTop = anchor.bottom + gap;
    const aboveTop = anchor.top - gap - surface.height;
    const fitsBelow = belowTop + surface.height <= view.bottom;
    const fitsAbove = aboveTop >= view.top;
    const placement = fitsBelow || !fitsAbove ? "bottom" : "top";
    const desiredTop = placement === "bottom" ? belowTop : aboveTop;
    const maximumTop = Math.max(view.top, view.bottom - surface.height);
    const top = Math.max(view.top, Math.min(desiredTop, maximumTop));
    applyPosition(blockMenu, { left, top, placement });
  }

  function commandGroup(label, buttons) {
    const group = document.createElement("div");
    group.className = "context-menu__group";
    const heading = document.createElement("span");
    heading.className = "context-menu__label";
    heading.textContent = label;
    group.append(heading, ...buttons);
    return group;
  }

  function compactCommandGroup(label, className, buttons) {
    const group = commandGroup(label, []);
    const tools = document.createElement("div");
    tools.className = className;
    tools.append(...buttons);
    group.append(tools);
    return group;
  }

  function colorPaletteRow(label, buttons) {
    const row = document.createElement("div");
    row.className = "context-menu__palette-row";
    const rowLabel = document.createElement("span");
    rowLabel.className = "context-menu__palette-label";
    rowLabel.textContent = label;
    const palette = document.createElement("div");
    palette.className = "context-menu__palette";
    palette.append(...buttons);
    row.append(rowLabel, palette);
    return row;
  }

  function renderBlockMenu(next) {
    const view = viewportRect(readViewport);
    if (disposed || !blockMenuOpen || !next?.focused || !next?.editable || !next.block
      || !rectIntersectsViewport(next.blockRect, view)) {
      blockMenu.hidden = true;
      blockMenuOpen = false;
      blockTypeTrigger.setAttribute("aria-expanded", "false");
      return;
    }
    const commands = safeCommands(editor, "block");
    const byId = new Map(commands.map((command) => [command.id, command]));
    const blockButton = (id, options = {}) => {
      const command = byId.get(id);
      return command ? makeCommandButton(command, "block", {}, options) : null;
    };
    const quickButtons = QUICK_BLOCK_TYPES
      .map((id) => blockButton(id, id === "paragraph"
        ? {}
        : { showLabel: true, hideIcon: true }))
      .filter(Boolean);
    const blockButtons = MENU_BLOCK_TYPES
      .map((id) => blockButton(id))
      .filter(Boolean);
    const alignmentButtons = MENU_ALIGNMENT_COMMANDS
      .map((id) => blockButton(id))
      .filter(Boolean);
    const textColor = byId.get("text-color");
    const backgroundColor = byId.get("background-color");
    const resetColors = byId.get("reset-colors");
    const serviceButtons = SERVICE_COMMANDS
      .map((id) => blockButton(id))
      .filter(Boolean);

    const quickTypes = document.createElement("div");
    quickTypes.className = "context-menu__quick-types";
    quickTypes.append(...quickButtons);
    const children = [quickTypes];
    if (blockButtons.length) {
      children.push(compactCommandGroup(
        "列表与块类型", "context-menu__primary-blocks", blockButtons
      ));
    }
    if (alignmentButtons.length) {
      children.push(compactCommandGroup(
        "对齐与缩进", "context-menu__primary-alignment", alignmentButtons
      ));
    }
    if (textColor || backgroundColor || resetColors) {
      const colors = commandGroup("颜色", []);
      const tools = document.createElement("div");
      tools.className = "context-menu__primary-colors";
      if (textColor) tools.append(colorPaletteRow(
        "文字", paletteButtons(textColor, "block", TEXT_COLORS)
      ));
      if (backgroundColor) tools.append(colorPaletteRow(
        "背景", paletteButtons(backgroundColor, "block", BACKGROUND_COLORS)
      ));
      if (resetColors) {
        const reset = makeCommandButton(resetColors, "block");
        reset.classList.add("context-menu__color-reset");
        tools.append(reset);
      }
      colors.append(tools);
      children.push(colors);
    }
    if (serviceButtons.length) {
      children.push(compactCommandGroup(
        "块操作", "context-menu__primary-actions", serviceButtons
      ));
    }

    const panel = document.createElement("div");
    panel.className = "context-menu__panel";
    panel.append(...children);
    blockMenu.replaceChildren(panel);
    blockMenu.hidden = false;
    blockTypeTrigger.setAttribute("aria-expanded", "true");
    placeBlockMenu(view);
  }

  function renderBlockTrigger(next) {
    const view = viewportRect(readViewport);
    const visible = next?.focused === true
      && next?.editable === true
      && next.block
      && rectIntersectsViewport(next.blockRect, view);
    blockTypeTrigger.hidden = !visible;
    if (!visible) {
      blockMenu.hidden = true;
      blockMenuOpen = false;
      blockTypeTrigger.setAttribute("aria-expanded", "false");
      return;
    }
    const presentation = getBlockTriggerPresentation(next.block);
    blockTypeTrigger.replaceChildren();
    blockTypeTrigger.setAttribute("aria-label", presentation.tooltip);
    blockTypeTrigger.title = presentation.tooltip;
    if (next.block.type === "heading" && presentation.label) {
      const label = document.createElement("span");
      label.className = "block-type-trigger__label";
      label.textContent = presentation.label;
      blockTypeTrigger.append(label);
    } else {
      blockTypeTrigger.append(createCommandIcon(document, presentation.icon));
    }
    const chevron = document.createElement("span");
    chevron.className = "editor-chevron";
    chevron.textContent = "▾";
    blockTypeTrigger.append(chevron);
    placeBlockTrigger(next, view);
  }

  function selectionCommandElements(byId) {
    const result = [];
    const directIds = ["bold", "strike", "italic", "underline", "link", "inline-code"];
    for (const group of SELECTION_GROUPS.slice(0, 3)) result.push(makeSelectionGroupOpener(group));
    const firstSeparator = document.createElement("span");
    firstSeparator.className = "selection-toolbar__separator";
    result.push(firstSeparator);
    for (const id of directIds) {
      const command = byId.get(id);
      if (command) result.push(makeCommandButton(command, "selection"));
    }
    const secondSeparator = document.createElement("span");
    secondSeparator.className = "selection-toolbar__separator";
    result.push(secondSeparator);
    for (const group of SELECTION_GROUPS.slice(3)) result.push(makeSelectionGroupOpener(group));
    const clearFormatting = byId.get("clear-formatting");
    if (clearFormatting) result.push(makeCommandButton(clearFormatting, "selection"));
    return result;
  }

  function renderSelectionPanel(byId) {
    selectionToolbarPanels.replaceChildren();
    if (!activeSelectionGroup) return;
    const group = SELECTION_GROUPS.find(({ id }) => id === activeSelectionGroup);
    const opener = selectionToolbarCommands.querySelector(`[data-selection-group="${activeSelectionGroup}"]`);
    if (!group || !opener) {
      activeSelectionGroup = null;
      return;
    }
    const panel = makeSelectionPanel(group, byId);
    selectionToolbarPanels.append(panel);
    const toolbarRect = readSafeRect(selectionToolbar.getBoundingClientRect());
    const openerRect = readSafeRect(opener.getBoundingClientRect());
    if (!toolbarRect || !openerRect) return;
    const panelWidth = surfaceSize(panel).width;
    const maximumLeft = Math.max(0, toolbarRect.right - toolbarRect.left - panelWidth);
    const desiredLeft = openerRect.left - toolbarRect.left;
    panel.style.setProperty("--selection-popover-left", `${Math.max(0, Math.min(desiredLeft, maximumLeft))}px`);
  }

  function renderSelectionToolbar(next) {
    const view = viewportRect(readViewport);
    const visible = next?.focused === true
      && next?.editable === true
      && validSelection(next.selection)
      && rectIntersectsViewport(next.selectionRect, view);
    selectionToolbar.hidden = !visible;
    if (!visible) {
      activeSelectionGroup = null;
      selectionToolbarPanels.replaceChildren();
      return;
    }
    const commands = safeCommands(editor, "selection");
    const byId = new Map(commands.map((command) => [command.id, command]));
    selectionToolbarCommands.replaceChildren(...selectionCommandElements(byId));
    renderSelectionPanel(byId);
    aiBubble.replaceChildren(createCommandIcon(document, "ai"), document.createTextNode("AI"));
    aiBubble.setAttribute("aria-label", COMMAND_PRESENTATION.ai.tooltip);
    aiBubble.title = COMMAND_PRESENTATION.ai.tooltip;
    aiBubble.onclick = () => {
      if (disposed || !snapshot || !validSelection(snapshot.selection)) return;
      try { onAi(snapshot.selection); } catch {}
    };
    placeFloating(selectionToolbar, next.selectionRect, view);
  }

  function close() {
    epoch += 1;
    blockMenuOpen = false;
    activeSelectionGroup = null;
    blockMenu.hidden = true;
    selectionToolbar.hidden = true;
    selectionToolbarPanels.replaceChildren();
    slashMenu.hidden = true;
    blockTypeTrigger.setAttribute("aria-expanded", "false");
    try { onCloseSlash(); } catch {}
  }

  function toggleBlockMenu() {
    if (disposed || blockTypeTrigger.hidden || !snapshot) return;
    blockMenuOpen = !blockMenuOpen;
    if (!blockMenuOpen) {
      blockMenu.hidden = true;
      blockTypeTrigger.setAttribute("aria-expanded", "false");
      return;
    }
    renderBlockMenu(snapshot);
  }

  function reposition() {
    if (disposed || !snapshot || !scrollOrigins) return;
    const view = viewportRect(readViewport);
    const blockTriggerRect = translateRectByScroll(snapshot.blockRect, scrollOrigins.blockTrigger, window);
    const blockMenuRect = translateRectByScroll(snapshot.blockRect, scrollOrigins.blockMenu, window);
    const selectionRect = translateRectByScroll(snapshot.selectionRect, scrollOrigins.selectionToolbar, window);
    renderBlockTrigger({ ...snapshot, blockRect: blockTriggerRect });
    if (!rectIntersectsViewport(selectionRect, view)) selectionToolbar.hidden = true;
    else if (!selectionToolbar.hidden) placeFloating(selectionToolbar, selectionRect, view);
    if (blockMenuOpen && !blockMenu.hidden) {
      if (!rectIntersectsViewport(blockMenuRect, view)) {
        blockMenuOpen = false;
        blockMenu.hidden = true;
        blockTypeTrigger.setAttribute("aria-expanded", "false");
      } else {
        placeBlockMenu(view);
      }
    }
  }

  function failClosedResize() {
    snapshot = null;
    blockTypeTrigger.hidden = true;
    close();
  }

  function handleResize() {
    if (disposed || !readResizeContext) return failClosedResize();
    try {
      const next = readResizeContext();
      if (!next) return failClosedResize();
      update(next);
    } catch {
      failClosedResize();
    }
  }

  function syncAncestorScrollListeners() {
    const next = new Set([
      ...getScrollAncestors(blockTypeTrigger),
      ...getScrollAncestors(blockMenu),
      ...getScrollAncestors(selectionToolbar)
    ]);
    for (const ancestor of observedScrollAncestors) {
      if (!next.has(ancestor)) ancestor.removeEventListener("scroll", reposition);
    }
    for (const ancestor of next) {
      if (!observedScrollAncestors.has(ancestor)) ancestor.addEventListener("scroll", reposition);
    }
    observedScrollAncestors = next;
  }

  function handleOutsidePointer(event) {
    if (disposed) return;
    const target = event.target;
    if (blockTypeTrigger.contains(target) || blockMenu.contains(target) || selectionToolbar.contains(target) || slashMenu.contains(target)) return;
    close();
  }

  function handleKeyDown(event) {
    if (disposed || event.key !== "Escape") return;
    if (activeSelectionGroup) {
      activeSelectionGroup = null;
      renderSelectionToolbar(snapshot);
      return;
    }
    close();
  }

  function preventPointerBlur(event) {
    event.preventDefault();
  }

  blockTypeTrigger.addEventListener("click", toggleBlockMenu);
  for (const element of [blockTypeTrigger, blockMenu, selectionToolbar, slashMenu]) {
    element.addEventListener("pointerdown", preventPointerBlur);
  }
  document.addEventListener("pointerdown", handleOutsidePointer);
  document.addEventListener("keydown", handleKeyDown);
  window?.addEventListener?.("scroll", reposition);
  window?.addEventListener?.("resize", handleResize);
  syncAncestorScrollListeners();
  captureSurfaceScrollOrigins();

  function update(next) {
    if (disposed) return;
    epoch += 1;
    snapshot = next;
    syncAncestorScrollListeners();
    captureSurfaceScrollOrigins();
    if (next?.focused !== true || next?.editable !== true) close();
    renderBlockTrigger(next);
    renderSelectionToolbar(next);
    if (blockMenuOpen) renderBlockMenu(next);
  }

  function destroy() {
    if (disposed) return;
    close();
    epoch += 1;
    blockTypeTrigger.hidden = true;
    disposed = true;
    blockTypeTrigger.removeEventListener("click", toggleBlockMenu);
    for (const element of [blockTypeTrigger, blockMenu, selectionToolbar, slashMenu]) {
      element.removeEventListener("pointerdown", preventPointerBlur);
    }
    document.removeEventListener("pointerdown", handleOutsidePointer);
    document.removeEventListener("keydown", handleKeyDown);
    window?.removeEventListener?.("scroll", reposition);
    window?.removeEventListener?.("resize", handleResize);
    for (const ancestor of observedScrollAncestors) ancestor.removeEventListener("scroll", reposition);
    observedScrollAncestors.clear();
    aiBubble.onclick = null;
  }

  return { update, close, destroy };
}
